import { useCallback, useEffect, useRef, useState } from "react";
import {
  diariesApi,
  fitnessApi,
  lifeGoalsApi,
  nutritionApi,
} from "../../../api";
import { MISSION_ACTIVITY_EVENT } from "../../../missionEvents";
import { asRows } from "./recordsModel";
import {
  dimensionRecords,
  goalRecords,
  type DimensionRecord,
} from "./dimensionRecords";
import type { DimensionKey } from "./dimensions";

export function useDimensionRecords(dimension: DimensionKey, active: boolean) {
  const [records, setRecords] = useState<DimensionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const sequence = useRef(0);
  const reload = useCallback(async () => {
    const request = ++sequence.current;
    setLoading(true);
    const sources =
      dimension === "purpose"
        ? [{ name: "目标与复盘", read: () => lifeGoalsApi.getAll() }]
        : [
            { name: "笔记", read: () => diariesApi.getAll() },
            ...(dimension === "body"
              ? [
                  {
                    name: "饮食",
                    read: () => nutritionApi.searchMealHistory(),
                  },
                  {
                    name: "运动",
                    read: () => fitnessApi.getExerciseSessions(),
                  },
                ]
              : []),
          ];
    const results = await Promise.allSettled(
      sources.map((s) => Promise.resolve().then(s.read)),
    );
    if (request !== sequence.current) return;
    const rows = results.map((r) =>
      r.status === "fulfilled" ? asRows(r.value.data) : [],
    );
    setRecords(
      dimension === "purpose"
        ? goalRecords(rows[0])
        : dimensionRecords(dimension, rows[0], rows[1], rows[2]),
    );
    setError(
      results
        .flatMap((r, i) => (r.status === "rejected" ? [sources[i].name] : []))
        .join("、"),
    );
    setLoading(false);
  }, [dimension]);
  useEffect(() => {
    if (!active) return;
    reload();
    const requests = sequence;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = () => {
      clearTimeout(timer);
      timer = setTimeout(reload, 150);
    };
    window.addEventListener(MISSION_ACTIVITY_EVENT, refresh);
    return () => {
      requests.current++;
      clearTimeout(timer);
      window.removeEventListener(MISSION_ACTIVITY_EVENT, refresh);
    };
  }, [reload, active]);
  return { records, loading, error, reload };
}
